import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { getPriortiyCampaign } from "../../Store/Action";
import { TYPE_MAPPING, CampaignCard } from "./CampaignCard";
import { RootState, useAppDispatch } from "../../Store";
import NoneItem from "./NoneItem";
import { ChevronUp } from "lucide-react";
import clsx from "clsx";
import { useThemeClass } from "@/Hooks/useThemeClass";
import { useThemeAsset } from "@/Hooks/useThemeAsset";
import { useTheme } from "styled-components";
import FamtourList, { FAMTOUR_ID } from "@/Screens/FamtourList.tsx";
const appTheme = import.meta.env.VITE_APP_THEME;

export type CardProps = {
  ctgId?: number;
  campaignTypeId?: string | number;
};

function useWindowSize() {
  const [windowSize, setWindowSize] = useState({
    width: typeof window !== 'undefined' ? window.innerWidth : 0,
    height: typeof window !== 'undefined' ? window.innerHeight : 0,
  });

  useEffect(() => {
    const handleResize = () => {
      // Update langsung tanpa debounce/throttle
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    // Pasang event listener
    window.addEventListener('resize', handleResize);
    
    // Bersihkan saat komponen unmount
    return () => window.removeEventListener('resize', handleResize);
  }, []); // Empty array = hanya di-mount sekali

  return windowSize;
}

const Card = ({ ctgId, campaignTypeId }: CardProps) => {
  if (ctgId === FAMTOUR_ID) {
    return <FamtourList />;
  }

  const theme = useTheme();
  const themeClass = useThemeClass();
  const themeAsset = useThemeAsset();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const { searchText } = useSelector(
    (state: RootState) => state.ReducerPriorityCampign
  );
  const dataCampaign = useSelector(
    (state: RootState) =>
      state.ReducerPriorityCampign.priortiyCampaignData?.data || []
  );
  const totalData = useSelector(
    (state: RootState) =>
      state.ReducerPriorityCampign.priortiyCampaignData?.totalRows || 0
  );

  const isHidden = location.pathname === "/";
  const [searchTimestamp, setSearchTimestamp] = useState(0);
  const [campaigns, setCampaigns] = useState([]);
  const [activeTypes, setActiveTypes] = useState([
    "blog",
    "instagram",
    "tiktok",
    "youtube",
  ]);
  const [filter, setFilter] = useState({
    type: "all",
    size: 20,
    page: 1,
    keyword: "",
    campaign_type: [1, 2, 3, 4, 5],
    ctg_id: ctgId,
    advertiser_id: null,
    id_reference: campaignTypeId,
  });
  const [isFetching, setIsFetching] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [isDataInitialized, setIsDataInitialized] = useState(false);
  const [showButton, setShowButton] = useState(false);
 const { width: windowWidth } = useWindowSize();
const campaignType = useMemo(() => {
  const newCampaignType = activeTypes.map((t) => TYPE_MAPPING[t]);
  return [...newCampaignType, 5]; // Always include type 5
}, [activeTypes]);

   const famtourMaxItem = useMemo(() => {
    if (windowWidth >= 1280) return 6;
    if (windowWidth >= 1024) return 4;
    return 4;
  }, [windowWidth]);

  // Sync URL params and search text
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const searchParam = params.get("q") || "";
    const advIdParam = params.get("adv_id");

    if (searchParam !== searchText) {
      dispatch({
        type: "SET_SEARCH_TEXT",
        payload: searchParam,
        meta: { immediateSearch: true },
      });
    }

    setFilter((prev) => ({
      ...prev,
      advertiser_id: advIdParam || null,
      page: 1, // Reset to first page when search or advertiser changes
    }));
  }, [location.search, dispatch, searchText]);

  // Handle search text changes
  useEffect(() => {
    setFilter((prev) => ({
      ...prev,
      keyword: searchText,
      page: 1, // Reset to first page when search changes
    }));
    setCampaigns([]);
    setIsDataInitialized(false);
    setSearchTimestamp(Date.now());
  }, [searchText]);

  // Fetch data when filter changes
  useEffect(() => {
    const controller = new AbortController();
    let isMounted = true;

    const fetchData = async () => {
      if (!isMounted) return;

      if (filter.page === 1) {
        setCampaigns([]);
      }

      setIsFetching(true);
      try {
        await dispatch(getPriortiyCampaign(filter));
      } finally {
        if (isMounted) {
          setIsFetching(false);
        }
      }
    };

    fetchData();

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [filter, dispatch]);

 // Ganti useEffect untuk activeTypes dengan menggunakan useMemo untuk campaign_type


// Kemudian dalam useEffect untuk filter, gunakan dependency campaignType
useEffect(() => {
  setFilter((prev) => ({
    ...prev,
    campaign_type: campaignType,
    page: 1, // Reset to first page when types change
    ctg_id: ctgId,
    id_reference: campaignTypeId,
  }));
}, [campaignType, ctgId, campaignTypeId]); // Hanya depend pada nilai-nilai ini
  
  // Update campaigns when new data arrives
  useEffect(() => {
    if (!dataCampaign) return;

    setCampaigns((prevCampaigns) => {
      if (filter.page === 1) {
        return dataCampaign;
      }

      const existingIds = new Set(prevCampaigns.map((item) => item.id));
      const newData = dataCampaign.filter((item) => !existingIds.has(item.id));

      return [...prevCampaigns, ...newData];
    });

    setHasMore(dataCampaign.length === filter.size && dataCampaign.length > 0);
    setIsDataInitialized(true);
  }, [dataCampaign, filter.page, filter.size]);

  // Handle type filter changes
  const handleTypeClick = (type: string) => {
    const newActiveTypes = activeTypes.includes(type)
      ? activeTypes.filter((t) => t !== type)
      : [...activeTypes, type];

    const newCampaignTypes = newActiveTypes.map((t) => TYPE_MAPPING[t]);

    setActiveTypes(newActiveTypes);
    setFilter((prev) => ({
      ...prev,
      campaign_type: [...newCampaignTypes, 5], // Always include type 5
      page: 1,
    }));
  };

  // Infinite scroll observer
  const observer = useRef<IntersectionObserver>();
  const lastCampaignRef = useCallback(
    (node: HTMLElement | null) => {
      if (isFetching || !hasMore) return;
      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting && hasMore && !isFetching) {
            setFilter((prev) => ({ ...prev, page: prev.page + 1 }));
          }
        },
        { rootMargin: "100px", threshold: 0.1 }
      );

      if (node) observer.current.observe(node);
    },
    [isFetching, hasMore]
  );

  // Sort handler
  const handleSortChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const sortMapping = {
      latest: "reg_start_date",
      popular: "total_apply",
      deadline: "announcement",
    };
    
    setFilter((prev) => ({
      ...prev,
      type: sortMapping[event.target.value as keyof typeof sortMapping],
      page: 1,
    }));
  };

  // Scroll to top button visibility
  useEffect(() => {
    const handleScroll = () => {
      setShowButton(window.scrollY > 300);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Determine which campaigns to display
  const displayedCampaigns = useMemo(() => {
    return location.pathname === "/mypage" 
      ? campaigns.slice(0, 3) 
      : campaigns;
  }, [campaigns, location.pathname]);

  return (
    <>
      <div className="relative">
        {/* Famtour section - only shown on homepage for weu theme */}
        {location.pathname === "/" && appTheme === "weu" && (
          <FamtourList  maxItem={famtourMaxItem}
          key={`famtour-${famtourMaxItem}`} />
        )}

        {/* Campaign header with sort and filter options */}
        <div
          id="recent"
          className={clsx(
            "text-xl mb-1 text-[#1D2C59] flex justify-between items-center",
            "sticky top-[56px] bg-white z-10 py-1"
          )}
        >
          <div
            className={clsx(
              location.pathname === "/mypage" ||
                location.pathname === "/interest-campaign"
                ? "hidden"
                : "absolute inset-0 w-screen left-1/2 -translate-x-1/2 -z-10",
              themeClass.bg("bgRecent")
            )}
          />

          {location.pathname === "/" ? (
            <div  className={clsx(!themeAsset.grayscaleLogo && "")}>
              <span className={themeClass.text("text3", "[#0F1E6C]")}>신규</span>
              <span className={clsx(themeClass.text("text3", "[#0F1E6C]"), "ml-1")}>
                캠페인
              </span>
            </div>
          ) : location.pathname === "/mypage" ||
            location.pathname === "/interest-campaign" ? null : (
            <div>
              <select
                className="ps-2 text-sm rounded py-1 border-gray-300 text-gray-700"
                onChange={handleSortChange}
                defaultValue="latest"
              >
                <option value="latest">최신순</option>
                <option value="popular">인기순</option>
                <option value="deadline">마감임박순</option>
              </select>
            </div>
          )}

          {/* Type filters */}
          {location.pathname !== "/mypage" &&
            location.pathname !== "/interest-campaign" && (
              <div className="flex gap-2 md:gap-4">
                {Object.entries(themeAsset.typeIcons)
                  .filter(([key]) => key !== "all")
                  .map(([key, { active }]) => (
                    <button
                      key={key}
                      className="flex items-center py-2 rounded-lg cursor-pointer focus:outline-none"
                      onClick={() => handleTypeClick(key)}
                      aria-label={`Toggle ${key} filter`}
                    >
                      <img
                        src={active}
                        className={`w-7 h-7 rounded-full transition-all ${
                          activeTypes.includes(key)
                            ? ""
                            : "grayscale opacity-50"
                        }`}
                        alt={key}
                      />
                    </button>
                  ))}
              </div>
            )}
        </div>

        {/* Campaign list */}
        {!isDataInitialized ? (
          <div className="grid grid-cols-3 lg:grid-cols-4 gap-3 xl:grid-cols-6 xl:gap-9 xl:mt-5 animate-pulse">
            {[...Array(12)].map((_, i) => (
              <div key={i} className="bg-gray-200 rounded-lg h-64"></div>
            ))}
          </div>
        ) : campaigns.length > 0 ? (
          <div className="grid grid-cols-3 lg:grid-cols-4 gap-3 xl:grid-cols-6 xl:gap-9 xl:mt-5">
            {displayedCampaigns.map((campaign, index) => (
              <CampaignCard
                key={campaign.id}
                campaign={campaign}
                index={index}
                ref={
                  index === displayedCampaigns.length - 1
                    ? lastCampaignRef
                    : null
                }
              />
            ))}
          </div>
        ) : (
          <NoneItem />
        )}

        {/* Loading indicator */}
        {location.pathname !== "/mypage" && (
          <div className="min-h-[100px] flex justify-center items-center">
            {isFetching && hasMore && (
              <div
                className={clsx(
                  themeClass.border("text"),
                  "w-10 h-10 border-t-2 border-b-2 rounded-full animate-spin"
                )}
              />
            )}
          </div>
        )}
      </div>

      {/* Scroll to top button */}
      {showButton && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-[5%] right-5 bg-white text-black p-3 rounded-full shadow-lg hover:bg-[#46B6D0] transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
          aria-label="Scroll to top"
        >
          <ChevronUp size={24} />
        </button>
      )}
    </>
  );
};

export default Card;